import React, { useContext, useState } from 'react';
import ReactPDF, {
  ClipPath,
  Defs,
  Font,
  G,
  Image,
  Link,
  Path,
  pdf,
  StyleSheet,
  Svg,
  Text,
  View
} from '@react-pdf/renderer';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dayjs, { Dayjs } from 'dayjs';
import { EChartsInstance } from 'echarts-for-react';

import { NonEmptyDateRange } from '@mui/x-date-pickers-pro/internal/models';

import { getAggregatedStats } from '../apis/dashboard';
import soleilExtrabold from '../assets/fonts/Soleil-Extrabold.woff';
import soleilRegular from '../assets/fonts/Soleil-Regular.woff';
import { SessionContext } from '../auth';
import useAsyncEffect from '../hooks/useAsyncEffect';
import { HeaderContext } from '../layouts/main/header/context';
import { IAggregatedStats } from '../types/global';
import { IProfile, IProfileDomain } from '../types/users';
import { makeFormatCurrency, makeFormatPercent, useFullYearFormat, useNoYearDateFormat } from '../utils/format';
import sleep from '../utils/sleep';

import { formatValue } from './DateRangePicker';

Font.register({
  family: 'Soleil',
  fonts: [
    { src: soleilRegular },
    { src: soleilExtrabold, fontWeight: 800 },
  ],
});

export function MetricalLogo(props: ReactPDF.SVGProps) {
  return (
    <Svg width="479.967" height="87.474" viewBox="0 0 479.967 87.474" {...props}>
      <Defs>
        <ClipPath id="a">
          <Path d="M0 65.606h359.975V0H0Z"/>
        </ClipPath>
      </Defs>
      <G clip-path="url(#a)" transform="matrix(1.33333 0 0 -1.33333 0 87.474)">
        <Path d="M0 0v21.142c0 4.181-.789 8.126-5.128 8.126-4.418 0-6.311-3.945-6.311-9.309V0h-12.623v21.142c0 4.812-1.103 8.126-5.205 8.126-4.418 0-6.312-3.945-6.312-9.309V0h-12.622v38.892h12.622l-.078-5.601.157.078c1.973 3.235 5.759 6.233 10.729 6.233 5.365 0 9.625-1.815 11.596-7.1 2.762 4.733 6.627 7.1 12.386 7.1 9.939 0 13.49-6.785 13.49-16.172V0Z" fill="#fff" transform="translate(118.525 5.543)" />
        <Path d="M0 0c-5.206 0-7.889-4.103-8.046-7.81H7.968C7.889-4.26 5.286 0 0 0m19.564-14.989H-8.125c.552-5.601 4.812-8.125 9.939-8.125 5.049 0 9.467 2.209 11.676 3.708l4.97-6.548C17.198-26.9 12.07-32.266 0-32.266c-11.991 0-20.747 7.416-20.747 20.275C-20.747.868-11.28 8.204.237 8.204c11.518 0 19.486-7.652 19.486-19.012 0-1.893-.159-4.181-.159-4.181" fill="#fff" transform="translate(156.543 36.94)" />
        <Path d="M0 0v-13.963c0-3.392.236-5.759 4.103-5.759 1.971 0 3.786.71 3.786.71l.631-9.861c-.947-.236-4.417-1.263-8.994-1.263-10.097 0-12.227 6.154-12.227 15.069V0h-5.523v9.545h5.523v10.571H0V9.545h8.599V0Z" fill="#fff" transform="translate(197.089 34.811)" />
        <Path d="M0 0h-.553c-.71.395-2.366.947-4.575.947-6.232 0-7.81-5.838-7.81-10.966v-17.276h-12.623v38.892h12.623l-.079-5.523h.158c2.209 4.261 5.443 6.233 9.625 6.233 2.602 0 3.944-.71 3.944-.71z" fill="#fff" transform="translate(236.689 32.838)" />
        <Path d="M242.208 44.435h12.622V5.543h-12.622z" fill="#fff" />
        <Path d="M0 0c-10.65 0-20.511 7.258-20.511 20.195 0 13.017 10.019 20.275 20.668 20.275 9.941 0 14.437-4.102 14.437-4.102l-2.761-9.546a15.99 15.99 0 0 1-10.019 3.471c-5.364 0-9.624-3.707-9.624-9.86 0-6.154 3.866-10.019 9.782-10.019 5.917 0 9.861 3.786 9.861 3.786l2.998-9.545C14.437 4.339 10.334 0 0 0" fill="#fff" transform="translate(280.388 4.675)" />
        <Path d="M0 0c-3.866-.158-6.153-1.814-6.153-4.733 0-2.998 2.445-4.813 5.916-4.813 3.392 0 5.68 2.209 6.468 3.55V.395zm6.705-16.725-.236 4.103c-1.657-1.657-4.813-4.971-11.123-4.971-6.864 0-14.121 3.708-14.121 12.386 0 8.678 7.967 11.597 15.146 11.912l9.782.395v.868c0 4.101-2.919 6.231-7.967 6.231-4.97 0-9.941-2.209-11.913-3.47l-3.313 8.204c3.313 1.657 9.388 3.944 16.645 3.944 12.149 0 19.17-5.049 19.17-17.671v-21.931z" fill="#fff" transform="translate(316.895 22.268)" />
        <Path d="M341.031 62.027h12.622V5.736h-12.622z" fill="#fff" />
        <Path d="M0 0v-33.358h13.473V6.173Z" fill="#fff" transform="translate(5.98 38.902)" />
        <Path d="M0 0v-42.176h13.932V6.173Z" fill="#fff" transform="translate(24.677 47.72)" />
        <Path d="M0 0v-49.273h13.932V7.21Z" fill="#fff" transform="translate(43.834 54.817)" />
        <Path d="M0 0a7.153 7.153 0 0 0-7.154-7.153A7.154 7.154 0 1 0 0 0" fill="#fff" transform="translate(255.674 55.145)" />
      </G>
    </Svg>
  );
}

const styles = StyleSheet.create({
  topBar: {
    height: '39pt',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20pt',
    backgroundColor: '#6A7AB8',
    marginBottom: '10pt'
  },
  metricalLogo: {
    height: '14pt',
    width: '76.8pt',
    marginRight: '15pt',
  },
  customerLogo: {
    height: '23pt',
  },
  header: {
    marginTop: '17pt',
    marginBottom: '18pt',
    fontSize: '24pt',
    lineHeight: 1.25,
    color: '#4559A7',
  },
  documentHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  shortLink: {
    fontSize: '9pt',
    color: '#4559A7',
  },
  headerLogos: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lifetimeStats: {
    flexDirection: 'row',
  },
  lifetimeStat: {
    paddingRight: '8pt',
  },
  lifetimeStatValue: {
    fontSize: '11pt',
    fontWeight: 800,
    color: 'white',
  },
  lifetimeStatLabel: {
    fontSize: '6pt',
    color: 'white',
    textTransform: 'uppercase',
    marginTop: 'auto',
  },
  metaRow: {
    flexDirection: 'row',
    color: '#4559A7',
    fontSize: '9pt',
    marginBottom: '4pt',
  },
  rowLabel: {
    textTransform: 'uppercase',
    width: '71pt',
  },
  footer: {
    fontSize: '7pt',
    color: '#6A7AB8',
    textTransform: 'uppercase',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: '18pt',
  },
});

export interface CommonHeaderProps {
  customerLogo: string | null;
  domainURL: string;
  domainName: string;
  lifetimeStats: IAggregatedStats;
  orgStart: string;
  fullYearFormat: string;
  noYearFormat: string;
  profile: IProfile;
}

export interface HeaderProps extends CommonHeaderProps {
  shortLink: string;
  dateRange: NonEmptyDateRange<Dayjs> | null;
}

export function useHeaderProps(): CommonHeaderProps | null {
  const { profile } = useContext(SessionContext) as { profile: IProfile };
  const { domainId, dashboardType, organizationId } = useContext(HeaderContext);
  const { logoUrl: customerLogo, url: domainURL, name: domainName } = profile.organizations
    .flatMap((x) => x.domains)
    .find((x) => x.id === domainId) as IProfileDomain;
  const fullYearFormat = useFullYearFormat();
  const noYearFormat = useNoYearDateFormat();
  const [lifetimeStats, setLifetimeStats] = useState<IAggregatedStats | null>(null);
  useAsyncEffect(async () => {
    const { data } = await getAggregatedStats(domainId, dashboardType);
    setLifetimeStats(data);
  }, [domainId, dashboardType]);
  const { start: orgStart } = profile.organizations
    .find((x) => x.id === organizationId)!
    .domains.find((x) => x.id === domainId) as IProfileDomain;
  if (!lifetimeStats) {
    return null;
  }
  return {
    customerLogo, domainURL, domainName, fullYearFormat, noYearFormat, lifetimeStats, orgStart, profile,
  };
}

export function Header({
  customerLogo, domainURL, domainName, lifetimeStats, orgStart, shortLink, profile, dateRange, fullYearFormat, noYearFormat,
}: HeaderProps) {
  const formatCurrency = makeFormatCurrency(profile);
  const formatCurrency2 = makeFormatCurrency(profile, 2);
  const formatPercent = makeFormatPercent(profile);
  const formattedDateRange = dateRange && formatValue(dateRange, fullYearFormat, noYearFormat);
  return (
    <>
      <View style={styles.topBar} fixed>
        <View style={styles.headerLogos}>
          <MetricalLogo
            style={styles.metricalLogo}
          />
          {customerLogo && (
            <Image
              src={{
                uri: customerLogo,
                method: 'GET',
                headers: {
                  'Cache-Control': 'no-cache',
                },
                body: '',
              }}
              style={styles.customerLogo}
            />
          )}
        </View>
        <View style={styles.lifetimeStats}>
          {'incrementalRevenue' in lifetimeStats ? (
            <>
              <View style={styles.lifetimeStat}>
                <View style={styles.lifetimeStatValue}>
                  <Text>
                    {formatCurrency({ value: lifetimeStats.incrementalRevenue })}
                  </Text>
                </View>
                <View style={styles.lifetimeStatLabel}>
                  <Text>
                    Net new revenue
                  </Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View style={styles.lifetimeStat}>
                <Text style={styles.lifetimeStatValue}>
                  {formatCurrency({ value: lifetimeStats.grossRevenue })}
                </Text>
                <View style={styles.lifetimeStatLabel}>
                  <Text>
                    Gross revenue
                  </Text>
                </View>
              </View>
              <View style={styles.lifetimeStat}>
                <Text style={styles.lifetimeStatValue}>
                  {formatCurrency2({ value: lifetimeStats.revenuePerVisit })}
                </Text>
                <View style={styles.lifetimeStatLabel}>
                  <Text>
                    Revenue per visit
                  </Text>
                </View>
              </View>
            </>
          )}
          <View>
            <View style={styles.lifetimeStatLabel}>
              <Text>
                {`since ${dayjs(orgStart).format('MMM YYYY')}`}
              </Text>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.documentHeaderRow}>
        <Text style={styles.header}>
          Performance Report
        </Text>
        <Link
          src={shortLink}
          style={styles.shortLink}
        >
          {shortLink}
        </Link>
      </View>
      <View style={styles.metaRow}>
        <Text style={styles.rowLabel}>
          Domain:
        </Text>
        <Text>
          {domainName} ({domainURL})
        </Text>
      </View>
      {dateRange && (
        <View style={styles.metaRow}>
          <Text style={styles.rowLabel}>
            Timeframe:
          </Text>
          <Text>
            {formattedDateRange}
          </Text>
        </View>
      )}
    </>
  )
}

export function Footer () {
  return (
    <View fixed style={styles.footer}>
      <Text
        render={({ pageNumber, totalPages }) => (
          `Page ${pageNumber} of ${totalPages}`
        )}
      />
      <Text>
        Copyright (c) {dayjs().format('YYYY')} Metrical Inc. All rights reserved
      </Text>
    </View>
  );
}

export interface ChartOptions {
  width: number;
  height: number;
}

export async function makeChartImage(chart: ChartJS | EChartsInstance, { width, height }: ChartOptions) {
  const [initialWidth, initialHeight] = [chart.width, chart.height];
  if (chart instanceof ChartJS) {
    /* eslint-disable no-param-reassign */
    // Store original configurations
    const originalYTitleSize = (chart.options.scales?.y as any)?.title?.font?.size;
    const originalYTitleColor = (chart.options.scales?.y as any)?.title?.color;
    const originalYTitleWeight = (chart.options.scales?.y as any)?.title?.font?.weight;
    const originalYTicksSize = (chart.options.scales?.y as any)?.ticks?.font?.size;
    const originalYTicksColor = (chart.options.scales?.y as any)?.ticks?.color;
    const originalYTicksWeight = (chart.options.scales?.y as any)?.ticks?.font?.weight;
    
    // Configure for PDF rendering
    if (chart.options.scales?.y) {
      if ((chart.options.scales.y as any).title) {
        if (!(chart.options.scales.y as any).title.font) {
          (chart.options.scales.y as any).title.font = {};
        }
        (chart.options.scales.y as any).title.font.size = 14;
        (chart.options.scales.y as any).title.color = '#4559A7';
        (chart.options.scales.y as any).title.font.weight = 'bold';
      }
      if ((chart.options.scales.y as any).ticks) {
        if (!(chart.options.scales.y as any).ticks.font) {
          (chart.options.scales.y as any).ticks.font = {};
        }
        (chart.options.scales.y as any).ticks.font.size = 14;
        (chart.options.scales.y as any).ticks.color = '#4559A7';
        (chart.options.scales.y as any).ticks.font.weight = 'bold';
      }
    }
    
    chart.options.devicePixelRatio = 4;
    chart.resize(width, height);
    await sleep(1);
    const image = chart.toBase64Image();
    
    // Restore original configurations
    if (chart.options.scales?.y) {
      if ((chart.options.scales.y as any).title?.font) {
        if (originalYTitleSize !== undefined) {
          (chart.options.scales.y as any).title.font.size = originalYTitleSize;
        }
        if (originalYTitleColor !== undefined) {
          (chart.options.scales.y as any).title.color = originalYTitleColor;
        }
        if (originalYTitleWeight !== undefined) {
          (chart.options.scales.y as any).title.font.weight = originalYTitleWeight;
        }
      }
      if ((chart.options.scales.y as any).ticks?.font) {
        if (originalYTicksSize !== undefined) {
          (chart.options.scales.y as any).ticks.font.size = originalYTicksSize;
        }
        if (originalYTicksColor !== undefined) {
          (chart.options.scales.y as any).ticks.color = originalYTicksColor;
        }
        if (originalYTicksWeight !== undefined) {
          (chart.options.scales.y as any).ticks.font.weight = originalYTicksWeight;
        }
      }
    }
    chart.options.devicePixelRatio = window.devicePixelRatio;
    chart.resize(initialWidth, initialHeight);
    /* eslint-enable no-param-reassign */
    return image;
  }
  const parent = chart.getDom().parentElement;
  parent.style.width = `${width}px`;
  parent.style.height = `${height}px`;
  chart.resize();
  await sleep(1);
  const image = chart.getDataURL({
    type: 'png',
    pixelRatio: 4,
    backgroundColor: 'transparent',
  });
  parent.style.width = '';
  parent.style.height = '';
  chart.resize();
  return image;
}

export function useDownloadPdf() {
  const { profile } = useContext(SessionContext) as { profile: IProfile };
  const { domainId } = useContext(HeaderContext);
  return async (doc: React.ReactElement, dateRange: NonEmptyDateRange<Dayjs>) => {
    const { url: domainURL } = profile.organizations
      .flatMap((x) => x.domains)
      .find((x) => x.id === domainId) as IProfileDomain;
    const blob = await pdf(doc).toBlob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const now = dayjs();
    const formattedRange = `${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}`;
    // Clean domain name by removing https:// and trailing slashes, ensure www. prefix
    let cleanDomainName = domainURL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    // Add www. prefix if not already present
    if (!cleanDomainName.startsWith('www.')) {
      cleanDomainName = `www.${cleanDomainName}`;
    }
    link.download = `Metrical - ${cleanDomainName} - ${formattedRange} - ${now.format('YYYYMMDD_HHmm')}.pdf`;
    link.click();
  };
}
